/**
 * CustomFieldsPage
 *
 * Standalone settings page for managing the account's custom field
 * schema — the same logic the per-course "Manage fields" dialog runs,
 * lifted into a dedicated surface so admins can find it without digging
 * into a course's settings tab.
 *
 * Definitions are account-scoped reference data (the lookups slice); every
 * mutation dispatches through it. Option edits change only the definition's
 * option list — a course keeps an already-saved value until it's re-saved.
 */

import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '../components/ui';
import {
  AddIcon,
  CloseIcon,
  DeleteIcon,
  EditIcon,
  LibraryBooksIcon,
} from '../components/icons';
import { PageHeader } from '../components/common';
import {
  CourseCustomField,
  CourseCustomFieldType,
  SaveCourseCustomFieldRequest,
} from '../apis/api-client';
import {
  createCustomField,
  deleteCustomField,
  selectAllCustomFields,
  updateCustomField,
  useEnsureCustomFields,
} from '../features/lookups/lookupsSlice';
import { useAppDispatch } from '../stores';

/**
 * Editable chip — click the label to enter rename mode (inline
 * TextField); X clears the chip via the supplied delete callback.
 */
function EditableChip({
  label,
  onRename,
  onDelete,
}: {
  label: string;
  onRename: (next: string) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);

  const commit = () => {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== label) onRename(next);
  };

  if (editing) {
    return (
      <TextField
        value={draft}
        autoFocus
        size="small"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') {
            setEditing(false);
            setDraft(label);
          }
        }}
        sx={{ width: 180 }}
      />
    );
  }
  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      onClick={() => {
        setDraft(label);
        setEditing(true);
      }}
      onDelete={onDelete}
      sx={{ cursor: 'pointer' }}
    />
  );
}

export default function CustomFieldsPage() {
  const dispatch = useAppDispatch();
  useEnsureCustomFields();
  const definitions = useSelector(selectAllCustomFields);

  const [editingNameId, setEditingNameId] = useState<number | null>(null);
  const [draftName, setDraftName] = useState('');
  const [optionDraft, setOptionDraft] = useState<Record<number, string>>({});

  // Inline "add new field" form.
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<CourseCustomFieldType>(CourseCustomFieldType.Text);
  const [newNameError, setNewNameError] = useState('');

  // Full-replace save — one request carries the complete definition.
  const saveField = (def: CourseCustomField, patch: Partial<SaveCourseCustomFieldRequest>) => {
    if (!def.id) return;
    dispatch(
      updateCustomField({
        id: def.id,
        request: {
          name: def.name,
          fieldType: def.fieldType,
          options: def.options,
          sequenceOrder: def.sequenceOrder,
          ...patch,
        },
      }),
    );
  };

  const startRename = (def: CourseCustomField) => {
    setEditingNameId(def.id ?? null);
    setDraftName(def.name ?? '');
  };
  const commitRename = (def: CourseCustomField) => {
    const trimmed = draftName.trim();
    if (!trimmed || trimmed === def.name) {
      setEditingNameId(null);
      return;
    }
    saveField(def, { name: trimmed });
    setEditingNameId(null);
  };

  const handleAddOption = (def: CourseCustomField) => {
    const draft = (optionDraft[def.id!] || '').trim();
    if (!draft) return;
    const opts = def.options ?? [];
    if (!opts.some((o) => o.toLowerCase() === draft.toLowerCase())) {
      saveField(def, { options: [...opts, draft] });
    }
    setOptionDraft((prev) => ({ ...prev, [def.id!]: '' }));
  };
  const handleRenameOption = (def: CourseCustomField, oldName: string, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) return;
    const opts = def.options ?? [];
    const next = opts
      .map((o) => (o === oldName ? trimmed : o))
      .filter((o, i, arr) => arr.findIndex((x) => x.toLowerCase() === o.toLowerCase()) === i);
    saveField(def, { options: next });
  };
  const handleRemoveOption = (def: CourseCustomField, option: string) => {
    const ok = window.confirm(
      `Remove "${option}" from "${def.name}"? Courses that already use it keep the value until they're re-saved.`,
    );
    if (!ok) return;
    saveField(def, { options: (def.options ?? []).filter((o) => o !== option) });
  };

  const handleDeleteField = (def: CourseCustomField) => {
    const ok = window.confirm(
      `Delete the "${def.name}" field? This removes it (and any saved values) from every course.`,
    );
    if (!ok || !def.id) return;
    dispatch(deleteCustomField(def.id));
  };

  const handleAddField = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      setNewNameError('Field name is required');
      return;
    }
    const existing = definitions.find(
      (f) => (f.name ?? '').toLowerCase() === trimmed.toLowerCase(),
    );
    if (existing) {
      setNewNameError('A field with this name already exists');
      return;
    }
    const request: SaveCourseCustomFieldRequest = {
      name: trimmed,
      fieldType: newType,
      options: newType === CourseCustomFieldType.Select ? [] : undefined,
      sequenceOrder: definitions.length,
    };
    await dispatch(createCustomField(request));
    setNewName('');
    setNewType(CourseCustomFieldType.Text);
    setNewNameError('');
    setAdding(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={<LibraryBooksIcon />}
        title="Content custom fields"
        description="Account-wide field schema that surfaces on every course's Custom Fields card."
      />

      {definitions.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            No custom fields defined yet. Add one to capture attributes
            like CE hours, professional role, or category — fields you
            add here apply to every course.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setAdding(true)}
          >
            Add custom field
          </Button>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {definitions.map((def) => {
            const isSelect = def.fieldType === CourseCustomFieldType.Select;
            const options = def.options ?? [];

            return (
              <Card key={def.id} sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {editingNameId === def.id ? (
                    <>
                      <TextField
                        value={draftName}
                        onChange={(e) => setDraftName(e.target.value)}
                        size="small"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename(def);
                          if (e.key === 'Escape') setEditingNameId(null);
                        }}
                        sx={{ flex: 1 }}
                      />
                      <Button size="small" variant="contained" onClick={() => commitRename(def)}>
                        Save
                      </Button>
                      <Button size="small" onClick={() => setEditingNameId(null)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                        {def.name}
                      </Typography>
                      <Chip
                        label={
                          isSelect
                            ? 'Multi-select'
                            : def.fieldType === CourseCustomFieldType.Number
                              ? 'Number'
                              : 'Text'
                        }
                        size="small"
                        variant="outlined"
                      />
                      <Tooltip title="Rename field">
                        <IconButton size="small" onClick={() => startRename(def)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete field">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteField(def)}
                          sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>

                {/* Option management — multi-select fields only. */}
                {isSelect && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        {options.length > 0
                          ? 'Options available on every course · click a chip to rename it, X to remove it.'
                          : 'No options yet — add some below to surface them on every course.'}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {options.map((opt) => (
                          <EditableChip
                            key={opt}
                            label={opt}
                            onRename={(next) => handleRenameOption(def, opt, next)}
                            onDelete={() => handleRemoveOption(def, opt)}
                          />
                        ))}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        placeholder="Add option (e.g. Front Desk, Dentist)"
                        value={optionDraft[def.id!] || ''}
                        onChange={(e) =>
                          setOptionDraft((prev) => ({ ...prev, [def.id!]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddOption(def);
                          }
                        }}
                        sx={{ flex: 1, maxWidth: 320 }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddOption(def)}
                        disabled={!(optionDraft[def.id!] || '').trim()}
                      >
                        Add option
                      </Button>
                    </Box>
                  </>
                )}
              </Card>
            );
          })}

          {/* Add-new row — sits at the bottom so the existing fields
              keep their visual rhythm at the top. */}
          {adding ? (
            <Card sx={{ p: 2.5, borderStyle: 'dashed', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                <TextField
                  label="Field name"
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    if (newNameError) setNewNameError('');
                  }}
                  placeholder="e.g. CE Hours, Professional Role, Category"
                  error={!!newNameError}
                  helperText={newNameError}
                  size="small"
                  sx={{ flex: 1, minWidth: 220 }}
                />
                <TextField
                  select
                  label="Type"
                  value={newType}
                  onChange={(e) => setNewType(Number(e.target.value) as CourseCustomFieldType)}
                  size="small"
                  sx={{ minWidth: 160 }}
                  SelectProps={{ native: true }}
                >
                  <option value={CourseCustomFieldType.Text}>Text</option>
                  <option value={CourseCustomFieldType.Number}>Number</option>
                  <option value={CourseCustomFieldType.Select}>Multi-select</option>
                </TextField>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleAddField}
                  sx={{ mt: 0.25 }}
                >
                  Add
                </Button>
                <IconButton
                  size="small"
                  onClick={() => {
                    setAdding(false);
                    setNewName('');
                    setNewNameError('');
                  }}
                  sx={{ mt: 0.25 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Card>
          ) : (
            <Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAdding(true)}
              >
                Add custom field
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
